import React from 'react';
import {BrowserRouter as Router, Route, Switch} from 'react-router-dom';
import AdReceived from './ad/AdReceived';
import CreateAd from './ad/CreateAd';
import EditAd from './ad/EditAd';
import AdInvoice from './ad/AdInvoice/AdInvoice';
import ListUserAds from './ad/ListUserAds';
import Description from './Description';
import NotFound from './errors/NotFound';
import {ProgramAds} from './ad/ProgramAds';
import ApproveAds from './ad/ApproveAds';
import ListAds from './ad/ListAds';
import PayAds from './ad/PayAds';
import ExportForm from './billing/ExportForm';
import HeadedContent from './common/HeadedContent';
import UserContext, {User} from './common/UserContext';
import SessionGuard from './common/SessionGuard';
import AuthorizedContent from './common/AuthorizedContent';
import {useContext} from 'react';
import {useIsAdmin} from '../hooks/useIsAdmin';

const Main: React.FC = () => {
  const {signOut, user, apiToken, secondsRemainingInSession} = useContext(UserContext);
  // Less than optimal experience with useIsAdmin hook in this component
  // because it's a wrapper arount a useEffect hook which takes some time
  // to resolve. In the meantime, the isAdmin value defaults to false and the page
  // initially renders with the "access denied" messages.
  const isAdmin = useIsAdmin(apiToken);
  const isLoggedIn = Boolean(user) && Boolean(apiToken);

  return (
    <SessionGuard
      signOut={signOut}
      isLoggedIn={isLoggedIn}
      secondsRemaining={secondsRemainingInSession}
    >
      <Router>
        <Switch>
          <Route exact path="/">
            <Description />
            <ListUserAds user={user}
                          apiToken={apiToken} />
          </Route>

          <Route path="/new" component={CreateAd} />

          <Route path="/edit/:key" component={EditAd} />

          <Route path="/invoice/:key" component={AdInvoice} />

          <Route path="/received" component={AdReceived} />

          <Route path="/admin/pay">
            <AuthorizedContent isAuthorized={isAdmin}>
              <HeadedContent title="Pay Ads">
                <PayAds user={user as User}
                        apiToken={apiToken || 'undefined-token'} />
              </HeadedContent>
            </AuthorizedContent>
          </Route>

          <Route path="/admin/approve">
            <AuthorizedContent
              isAuthorized={isAdmin}
              unauthorizedRender={<p>Not authorized</p>}
            >
              <HeadedContent title="Prepare Ads">
                <ApproveAds user={user as User}
                            apiToken={apiToken || 'undefined-token'} />
              </HeadedContent>
            </AuthorizedContent>
          </Route>

          <Route path="/admin/program">
            <AuthorizedContent
              isAuthorized={isAdmin}
              unauthorizedRender={<p>Not authorized</p>}
            >
              <HeadedContent title="Program Ads">
                <ProgramAds user={user as User}
                            apiToken={apiToken || 'undefined-token'} />
              </HeadedContent>
            </AuthorizedContent>
          </Route>

          <Route path="/admin/billing">
            <AuthorizedContent
              isAuthorized={isAdmin}
              unauthorizedRender={<p>Not authorized</p>}
            >
              <HeadedContent title="Billing Export">
                <ExportForm />
              </HeadedContent>
            </AuthorizedContent>
          </Route>

          <Route path="/admin">
            <AuthorizedContent
              isAuthorized={isAdmin}
              unauthorizedRender={<p>Not authorized</p>}
            >
              <HeadedContent title="All Ads">
                <ListAds apiToken={apiToken || 'undefined-token'} />
              </HeadedContent>
            </AuthorizedContent>
          </Route>

          <Route path="*">
            <NotFound />
          </Route>
        </Switch>
      </Router>
    </SessionGuard>
  );
};
export default Main;
